import React from "react"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import { Application } from "@/contexts/ApplicationContext"

interface AvailabilitySectionProps {
  application: Application
  setApplication: (app: Application) => void
  daysRangeOptions: Array<{
    value: string
    label: string
  }>
  weekDaysOptions: Array<{
    value: string
    label: string
  }>
}

const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({
  application,
  setApplication,
  daysRangeOptions,
  weekDaysOptions,
}) => {
  const getMaxDaysFromRange = (range: string): number => {
    switch (range) {
      case "1-3":
        return 3
      case "3-5":
        return 5
      case "5+":
        return 7
      default:
        return 7
    }
  }

  const maxDays = getMaxDaysFromRange(application.basicDetails.workDaysRange)
  const selectedDays = application.basicDetails.workDays.length

  const handleWorkDayToggle = (day: string) => {
    const isCurrentlySelected = application.basicDetails.workDays.includes(day)

    // If trying to select a new day and already at limit, don't allow it
    if (!isCurrentlySelected && selectedDays >= maxDays) {
      return
    }

    setApplication({
      ...application,
      basicDetails: {
        ...application.basicDetails,
        workDays: isCurrentlySelected
          ? application.basicDetails.workDays.filter((d) => d !== day)
          : [...application.basicDetails.workDays, day],
      },
    })
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="flex items-center px-6 pt-6">
        <h2 className="text-lg font-bold text-jungle">Availability</h2>
      </div>
      <Separator className="mt-2" />

      <div className="bg-transparent p-6">
        <div className="mb-6 space-y-3">
          <Label className="text-sm font-medium md:text-base">
            How many days per week are you looking to work?
          </Label>
          <RadioGroup
            value={application.basicDetails.workDaysRange}
            onValueChange={(value) => {
              // Clear selected days if new range is smaller than current selection
              const newMaxDays = getMaxDaysFromRange(value)
              const currentSelectedDays = application.basicDetails.workDays

              setApplication({
                ...application,
                basicDetails: {
                  ...application.basicDetails,
                  workDaysRange: value,
                  workDays:
                    currentSelectedDays.length > newMaxDays
                      ? currentSelectedDays.slice(0, newMaxDays)
                      : currentSelectedDays,
                },
              })
            }}
          >
            {daysRangeOptions.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={`days-${option.value}`} />
                <Label htmlFor={`days-${option.value}`} className="text-sm">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-medium md:text-base">
            Which days of the week are you looking to work?
          </Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {weekDaysOptions.map((option) => {
              const isSelected = application.basicDetails.workDays.includes(option.value)
              const isDisabled = !isSelected && selectedDays >= maxDays

              return (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${option.value}`}
                    checked={isSelected}
                    disabled={isDisabled}
                    onCheckedChange={() => handleWorkDayToggle(option.value)}
                  />
                  <label
                    htmlFor={`day-${option.value}`}
                    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${
                      isDisabled ? "text-gray-400 opacity-50" : "peer-disabled:opacity-70"
                    }`}
                  >
                    {option.label}
                  </label>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AvailabilitySection
